/**
 * Main entry point and initialization for Trakr Analytics Script
 */

import { parseConfig, validateConfig } from './config';
import { Tracker } from './tracking';
import { TraktksAPI } from './types';
import {
  shouldDisableTracking,
  validateDomain,
  debugLog,
  logWarning,
  logError
} from './utils';

(function() {
  'use strict';

  const config = parseConfig();
  if (!config) {
    return;
  }

  if (!validateConfig(config)) {
    return;
  }

  debugLog('Configuration', config, config.debugMode);

  const disableCheck = shouldDisableTracking(config.debugMode);
  if (disableCheck.disabled) {
    logWarning(`Tracking disabled: ${disableCheck.reason}`);
    return;
  }

  const domainValidation = validateDomain(config.domain);
  if (!domainValidation.isValid) {
    logWarning(domainValidation.error || 'Domain validation failed');
    return;
  }

  const tracker = new Tracker(config);

  /**
   * Global API:
   *   traktiks('pageview')
   *   traktiks('event', { type: 'signup', plan: 'pro' })
   *   traktiks('signup', { plan: 'pro' })      // shorthand: name + props
   *   traktiks.track('signup', { plan: 'pro' })
   */
  const traktiks: TraktksAPI = function(eventName: string, eventData?: Record<string, any>) {
    if (typeof eventName !== 'string') {
      logError('Event name must be a string');
      return;
    }

    switch (eventName.toLowerCase()) {
      case 'page':
      case 'pageview':
        tracker.trackPageview();
        break;
      case 'track':
      case 'event':
        if (eventData && typeof eventData === 'object') {
          const { type, ...data } = eventData;
          if (type) {
            tracker.trackCustomEvent(type, data);
          } else {
            logError('Event data must include a "type" property');
          }
        } else {
          logError('Event data must be an object with a "type" property');
        }
        break;
      default:
        tracker.trackCustomEvent(eventName, eventData);
        break;
    }
  };

  traktiks.track = function(eventType: string, eventData?: Record<string, any>) {
    tracker.trackCustomEvent(eventType, eventData);
  };

  (window as any).traktiks = traktiks;

  if (config.autoTrackPageview) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        tracker.trackPageview();
      });
    } else {
      tracker.trackPageview();
    }
  }

  if (config.trackOutbound) {
    document.addEventListener('click', function(event) {
      const target = event.target as HTMLElement;
      const linkElement = target.closest('a') as HTMLAnchorElement;
      if (linkElement) {
        tracker.trackExternalLink(linkElement);
      }
    });
  }

  debugLog('Trakr setup complete', {
    autoTrackPageview: config.autoTrackPageview,
    trackOutbound: config.trackOutbound
  }, config.debugMode);
})();
